import React from 'react';
import { cn } from '@/utils/cn';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'outlined' | 'elevated' | 'interactive';
  interactive?: boolean;
  as?: 'div' | 'article' | 'section';
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      interactive = false,
      as: Component = 'div',
      className,
      children,
      onClick,
      ...props
    },
    ref
  ) => {
    const isInteractive = interactive || Boolean(onClick);

    const baseStyles = `
      rounded-lg
      transition-all duration-200
    `;

    const variantStyles = {
      default: `
        bg-background
        border border-border
      `,
      outlined: `
        bg-transparent
        border-2 border-border
      `,
      elevated: `
        bg-background
        border border-border
        shadow-md
        hover:shadow-lg
      `,
      interactive: `
        bg-background
        border border-border
        shadow-sm
        cursor-pointer
        hover:shadow-md
        hover:border-primary/50
        active:scale-[0.98]
      `,
    };

    const interactiveStyles = isInteractive
      ? `
        cursor-pointer
        hover:shadow-md
        hover:border-primary/50
        active:scale-[0.98]
      `
      : '';

    return (
      <Component
        ref={ref}
        onClick={onClick}
        className={cn(
          baseStyles,
          variantStyles[variant],
          isInteractive && interactiveStyles,
          className
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Card.displayName = 'Card';

// CardHeader Component
interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  as?: 'div' | 'header';
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ as: Component = 'div', className, children, ...props }, ref) => {
    return (
      <Component
        ref={ref}
        className={cn('p-4 md:p-6 border-b border-border', className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

CardHeader.displayName = 'CardHeader';

// CardTitle Component
interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ as: Component = 'h3', className, children, ...props }, ref) => {
    return (
      <Component
        ref={ref}
        className={cn('text-lg md:text-xl font-semibold text-foreground', className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

CardTitle.displayName = 'CardTitle';

// CardSubtitle Component
interface CardSubtitleProps extends React.HTMLAttributes<HTMLParagraphElement> {}

const CardSubtitle = React.forwardRef<HTMLParagraphElement, CardSubtitleProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn('mt-1 text-sm text-foreground/60', className)}
        {...props}
      >
        {children}
      </p>
    );
  }
);

CardSubtitle.displayName = 'CardSubtitle';

// CardBody Component
interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {}

const CardBody = React.forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('p-4 md:p-6', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardBody.displayName = 'CardBody';

// CardFooter Component
interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  as?: 'div' | 'footer';
}

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ as: Component = 'div', className, children, ...props }, ref) => {
    return (
      <Component
        ref={ref}
        className={cn(
          'p-4 md:p-6 border-t border-border flex items-center justify-end gap-3',
          className
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

CardFooter.displayName = 'CardFooter';

export default Card;
export { Card, CardHeader, CardTitle, CardSubtitle, CardBody, CardFooter };
